/**
 * Filter bar that sits above the entries table:
 *   - Month / year selectors with prev/next arrows
 *   - Account dropdown (All accounts default)
 *   - Tag-filter pill row (All / Reconciled / Pending / Scheduled)
 *   - Conditional "Reconcile N selected" bulk-action button
 *
 * Pure view. All state lives in `useReconcile`; this component fires
 * back the setters/handlers passed as props.
 */
import React from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MONTHS, YEARS, TAG_FILTERS } from './constants';

const ReconcileFilters = ({
  year, month, setYear, setMonth, stepMonth,
  accounts, accountFilter, setAccountFilter,
  tagFilter, setTagFilter,
  selectedKeysSize, bulkReconcile, busy,
}) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-6" data-testid="reconcile-filters">
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <span className="print-only text-base font-bold text-[#020617]" data-testid="reconcile-period-label-print">
        {MONTHS[month - 1]?.label} {year}
      </span>
      <button
        type="button"
        onClick={() => stepMonth(-1)}
        className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] transition-colors print-hide"
        aria-label="Previous month"
        data-testid="prev-month-button"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div className="print-hide">
        <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
          <SelectTrigger className="w-40 bg-white" data-testid="month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => (<SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div className="print-hide">
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
          <SelectTrigger className="w-28 bg-white" data-testid="year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YEARS.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="button"
        onClick={() => stepMonth(1)}
        className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] transition-colors print-hide"
        aria-label="Next month"
        data-testid="next-month-button"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      <div className="ml-auto flex flex-wrap items-center gap-3">
        <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Account</Label>
        <Select value={accountFilter} onValueChange={setAccountFilter}>
          <SelectTrigger className="w-48 bg-white" data-testid="account-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            {accounts.map((acc) => (
              <SelectItem key={acc.account_id} value={acc.account_id}>{acc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-2">
      {TAG_FILTERS.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => setTagFilter(t.value)}
          className={`inline-flex items-center justify-center leading-none px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            tagFilter === t.value ? 'bg-[#0052FF] text-white' : 'bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]'
          }`}
          data-testid={`tag-filter-${t.value}`}
        >
          {t.label}
        </button>
      ))}
      <div className="ml-auto print-hide">
        {selectedKeysSize > 0 && (
          <Button
            onClick={bulkReconcile}
            disabled={busy}
            className="bg-[#10B981] text-white hover:bg-[#059669]"
            data-testid="bulk-reconcile-button"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Reconcile {selectedKeysSize} selected
          </Button>
        )}
      </div>
    </div>
  </div>
);

export default ReconcileFilters;
